/**
 * Element, status, player and UI colours (DESIGN 6.3) plus the cached colour
 * string helpers every render module uses. Canvas 2D pays for every fresh
 * `rgba(...)` string, so alpha variants and tints are memoised here; hot
 * loops call `elementRgba` / `rgba` / `mixHex` without allocating after the
 * first frame.
 *
 * No DOM here: the dev scene and tests import this file under the node lib.
 */

import type { DamageType, Element, EnemyKind, StatusId } from '@shared/index.js';

export interface ElementSwatch {
  /** Fill / stroke colour of the element itself (DESIGN 6.3 "Color"). */
  core: string;
  /** Secondary / particle colour (DESIGN 6.3). */
  soft: string;
  /** Dark edge for glyphs and rims so colour is never the only channel. */
  dark: string;
  rgb: readonly [number, number, number];
}

const swatch = (core: string, soft: string, dark: string): ElementSwatch => ({ core, soft, dark, rgb: hexToRgb(core) });

export const ELEMENT_SWATCH: Readonly<Record<Element, ElementSwatch>> = {
  water: swatch('#3B9DFF', '#BFE3FF', '#0D3F8A'),
  life: swatch('#4CE07A', '#D8FFE3', '#126A2C'),
  shield: swatch('#F5E663', '#FFF8C2', '#7A5E0F'),
  cold: swatch('#BFEFFF', '#FFFFFF', '#1B5E7A'),
  lightning: swatch('#C77DFF', '#FFFFFF', '#4A1E7A'),
  arcane: swatch('#FF3B3B', '#FF9B9B', '#6E0E18'),
  earth: swatch('#B5752D', '#6B4A1B', '#4A2C12'),
  fire: swatch('#FF7A1A', '#FFD166', '#7A2A00'),
  steam: swatch('#E6E6E6', '#FFFFFF', '#6B7C85'),
  ice: swatch('#8ED8FF', '#FFFFFF', '#176A82'),
  mud: swatch('#7A5A36', '#A88A62', '#3A2A18'),
  lava: swatch('#FF4A12', '#FFB347', '#5A1206'),
  spring: swatch('#5EE6C8', '#D6FFF4', '#0F5E52'),
  // Woven pieces (docs/spells-depth.md).
  plasma: swatch('#FF66E0', '#FFE0F8', '#6E1060'),
  flux: swatch('#9B5CFF', '#E2D4FF', '#3A1C80'),
  stillness: swatch('#A9C7D9', '#EEF6FB', '#3E5868'),
  obsidian: swatch('#5B4A78', '#B9A8DB', '#160E22'),
  ether: swatch('#B8A6FF', '#F3EEFF', '#4D3D99'),
  cloud: swatch('#C9D3DC', '#FFFFFF', '#5A6773'),
  ash: swatch('#8A7F78', '#D9CFC7', '#3A3330'),
  rime: swatch('#E3F7FF', '#FFFFFF', '#3F7F99'),
  moment: swatch('#FFD27A', '#FFF3D6', '#7A5A12'),
  dragonfire: swatch('#FF2E63', '#FFB08A', '#6A0820'),
  stormglass: swatch('#6B5BD6', '#D7D0FF', '#231A66'),
  thunderhead: swatch('#6E7FA8', '#DCE3F5', '#262F4A'),
  time: swatch('#E8B04A', '#FFEBC2', '#6B4710'),
  // slice 2
  tide: swatch('#1F6FE0', '#9FD0FF', '#0A2A66'),
  pulse: swatch('#7CFFB0', '#F0FFF5', '#1E7A4A'),
  void: swatch('#2A1840', '#8A6AB8', '#08040F'),
  wildfire: swatch('#FFA318', '#FFE7A0', '#7A3A00'),
  whirlpool: swatch('#2BB8C8', '#C8F4FA', '#0A4A55'),
  deluge: swatch('#3A5FA8', '#B8CCF0', '#14244A'),
  inferno: swatch('#E8241A', '#FFC07A', '#5A0A06'),
  glacier: swatch('#7FC4E8', '#F2FBFF', '#1F5A7A'),
  soul: swatch('#C8F0FF', '#FFFFFF', '#4A7A99'),
  sun: swatch('#FFE14A', '#FFFBE0', '#8A5A00'),
  winter: swatch('#D8ECFA', '#FFFFFF', '#2E5A80'),
  death: swatch('#4A4A52', '#C8C8D0', '#0E0E12'),
};

export function elementColor(e: Element): string { return ELEMENT_SWATCH[e].core; }
export function elementSecondary(e: Element): string { return ELEMENT_SWATCH[e].soft; }
export function elementDark(e: Element): string { return ELEMENT_SWATCH[e].dark; }

/** Robe colours for player slots 0..3 (DESIGN 6.3). */
export const PLAYER_COLORS: readonly string[] = ['#E04B4B', '#3C8BE0', '#45C26B', '#E0A33C'];
const PLAYER_COLORS_DARK: readonly string[] = ['#7A1F1F', '#17406F', '#1B5F32', '#704A12'];

export function playerColor(i: number): string {
  return PLAYER_COLORS[((i % PLAYER_COLORS.length) + PLAYER_COLORS.length) % PLAYER_COLORS.length] as string;
}
export function playerColorDark(i: number): string {
  return PLAYER_COLORS_DARK[((i % PLAYER_COLORS.length) + PLAYER_COLORS.length) % PLAYER_COLORS.length] as string;
}

export const STATUS_COLORS: Readonly<Record<StatusId, string>> = {
  wet: '#3B9DFF',
  burning: '#FF7A1A',
  chilled: '#BFEFFF',
  frozen: '#8ED8FF',
  shocked: '#C77DFF',
  knockdown: '#C9C9C9',
  hasted: '#F5E663',
  slowed: '#9AA0A6',
  mired: '#7A5A36',
  blinded: '#5A5148',
};
export function statusColor(id: StatusId): string { return STATUS_COLORS[id]; }

/** Floating number colours (DESIGN 6.4): white physical, element colour otherwise, green heals. */
export const DAMAGE_COLORS: Readonly<Record<DamageType, string>> = {
  physical: '#FFFFFF',
  arcane: ELEMENT_SWATCH.arcane.core,
  life: ELEMENT_SWATCH.life.core,
  fire: ELEMENT_SWATCH.fire.core,
  cold: ELEMENT_SWATCH.cold.core,
  lightning: ELEMENT_SWATCH.lightning.core,
  steam: ELEMENT_SWATCH.steam.core,
  water: ELEMENT_SWATCH.water.core,
};
export function damageColor(type: DamageType): string { return DAMAGE_COLORS[type]; }

export const UI = {
  bg: '#0E0F14',
  panel: 'rgba(23,25,35,0.82)',
  panelSolid: '#171923',
  panelEdge: 'rgba(255,255,255,0.08)',
  text: '#ECEFF4',
  muted: '#8A93A6',
  dim: '#4A5160',
  hpGreen: '#52E07A',
  hpYellow: '#F5E663',
  hpRed: '#FF4D5E',
  enemyHp: '#FF7A3C',
  bossHp: '#8ED8FF',
  ff: '#FF3B3B',
  gold: '#F5E663',
  white: '#FFFFFF',
  black: '#000000',
  shadow: 'rgba(0,0,0,0.45)',
  dead: '#6B6F7A',
} as const;

/**
 * The two PvP teams (capture the flag): names as on Twin Fonts' bases, and colours picked to sit apart from
 * every element's (no blue, green, gold, red, orange, purple or grey): a warm rose and a cool lime.
 */
export const TEAMS: readonly [{ name: string; color: string; dark: string }, { name: string; color: string; dark: string }] = [
  { name: 'Rose', color: '#FF5AA8', dark: '#7A1E4E' },
  { name: 'Lime', color: '#B6F24A', dark: '#4A6A12' },
];

export interface EnemySwatch { body: string; dark: string; accent: string }
export const ENEMY_COLORS: Readonly<Record<EnemyKind, EnemySwatch>> = {
  spider: { body: '#2B2B33', dark: '#121217', accent: '#FF3B3B' },
  rogue: { body: '#3F8B3A', dark: '#1E4A1C', accent: '#C9C9C9' },
  ranger: { body: '#6E8B3A', dark: '#36461C', accent: '#D8B38A' },
  bomber: { body: '#8B5A3A', dark: '#4A2C12', accent: '#FF7A1A' },
  shaman: { body: '#5B3A8B', dark: '#2C1A4A', accent: '#3B9DFF' },
  troll: { body: '#6B4A2B', dark: '#3A2612', accent: '#D8B38A' },
  treespirit: { body: '#2E7D3A', dark: '#163D1C', accent: '#7FD24A' },
  skeleton: { body: '#E8E8E0', dark: '#8A8A80', accent: '#2B2B33' },
  orc: { body: '#6E7480', dark: '#3A3E47', accent: '#B5B9C4' },
  jotunn: { body: '#2F5FA8', dark: '#162E5A', accent: '#BFEFFF' },
  dummy: { body: '#A8803F', dark: '#5C4420', accent: '#E8D8B0' },
  mire: { body: '#2F4A4A', dark: '#142626', accent: '#5FD4C8' },
};

export const GROUND = {
  void: '#07080B',
  floor: '#2A2D3A',
  floorAlt: '#2E3140',
  grout: '#23262F',
  wallTop: '#4A5366',
  wallFace: '#2B303C',
  wallEdge: '#5C667C',
  pond: '#0F2A5A',
  pondDeep: '#0A1E42',
  pondRim: '#1B4A8A',
  pondFrozen: '#BFEFFF',
  pillarTop: '#5A6275',
  pillarFace: '#3A4050',
  pillarBase: '#1E2129',
  gate: '#0C0D11',
  gateFrame: '#5C667C',
} as const;

// ------------------------------------------------------------------ cached string helpers

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.charCodeAt(0) === 35 ? hex.slice(1) : hex;
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, '$1$1') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const ALPHA_STEPS = 40;
const rgbaCache = new Map<string, string[]>();

/** `rgba()` of a hex colour at alpha quantised to 1/40 steps; strings are built once. */
export function rgba(hex: string, alpha: number): string {
  let steps = rgbaCache.get(hex);
  if (!steps) {
    steps = [];
    const [r, g, b] = hexToRgb(hex);
    for (let i = 0; i <= ALPHA_STEPS; i++) steps.push(`rgba(${r},${g},${b},${(i / ALPHA_STEPS).toFixed(3)})`);
    rgbaCache.set(hex, steps);
  }
  const i = alpha <= 0 ? 0 : alpha >= 1 ? ALPHA_STEPS : Math.round(alpha * ALPHA_STEPS);
  return steps[i] as string;
}

const elementRgbaCache: Partial<Record<Element, string[]>> = {};

/** Element core colour at the given alpha (cached, 1/40 steps). */
export function elementRgba(e: Element, alpha: number): string {
  let steps = elementRgbaCache[e];
  if (!steps) {
    steps = [];
    const [r, g, b] = ELEMENT_SWATCH[e].rgb;
    for (let i = 0; i <= ALPHA_STEPS; i++) steps.push(`rgba(${r},${g},${b},${(i / ALPHA_STEPS).toFixed(3)})`);
    elementRgbaCache[e] = steps;
  }
  const i = alpha <= 0 ? 0 : alpha >= 1 ? ALPHA_STEPS : Math.round(alpha * ALPHA_STEPS);
  return steps[i] as string;
}

const MIX_STEPS = 10;
const mixCache = new Map<string, string>();

/** Linear mix of two hex colours, k quantised to tenths (cached). Used for status tints and hit flashes. */
export function mixHex(a: string, b: string, k: number): string {
  const q = k <= 0 ? 0 : k >= 1 ? MIX_STEPS : Math.round(k * MIX_STEPS);
  if (q === 0) return a;
  if (q === MIX_STEPS) return b;
  const key = a + b + q;
  const hit = mixCache.get(key);
  if (hit) return hit;
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const t = q / MIX_STEPS;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  const out = `#${((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1).toUpperCase()}`;
  mixCache.set(key, out);
  return out;
}

/** Desaturate toward grey (frozen targets). */
export function greyed(hex: string, k: number): string {
  return mixHex(hex, '#8A8F9A', k);
}

/** HP bar colour by fraction: green -> yellow (<50%) -> red (<25%). */
export function hpColor(frac: number): string {
  return frac < 0.25 ? UI.hpRed : frac < 0.5 ? UI.hpYellow : UI.hpGreen;
}

/** Ping colour: green < 80 ms, yellow < 160 ms, red otherwise. */
export function pingColor(ms: number): string {
  return ms < 80 ? UI.hpGreen : ms < 160 ? UI.hpYellow : UI.hpRed;
}
